<template>
  <div>
    <provider-profile-error v-if="generalErrorType" :errorType="generalErrorType" />
    <div v-else>
      <provider-profile-banner v-if="bannerType" :bannerType="bannerType" />
      <provider-profile-container
        :profileAccountData="profileAccountData"
        :viewerAccountData="accountData"
        :reviewsData="reviewsData"
        :loading="loading"
        :bannerType="bannerType"
        :paramData="paramData"
        :accountTypeInfo="accountTypeInfo"
        @view-more-reviews="handleViewMoreReviews"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { mapGetters } from 'vuex';
import { BannerType, GeneralErrorType } from './__types__/ErrorTypes';
import { FormattedCareType } from './__types__/CareTypes';
import ProviderProfileError from './components/ProviderProfileError.vue';
import ProviderProfileBanner from './components/ProviderProfileBanner.vue';
import ProviderProfileContainer from './components/ProviderProfileContainer.vue';
import { profileQueryMap, FETCH_REVIEWS } from '@/graphql/provider/profile/queries';

export default {
  name: 'ProviderProfile',
  components: {
    ProviderProfileError,
    ProviderProfileBanner,
    ProviderProfileContainer,
  },
  data(): any {
    return {
      generalErrorType: GeneralErrorType.NONE,
      bannerType: BannerType.NONE,
      profileAccountData: null,
      reviewsData: null,
      loading: true,
      reviewsFetchCount: 3,
    };
  },
  computed: {
    ...mapGetters('account', ['getAccount']),
    accountData() {
      return this.getAccount;
    },
    paramData() {
      return {
        careType: this.$route.params.care_type,
        sitterUuid: this.$route.params.sitter_uuid,
      };
    },
    accountTypeInfo() {
      return {
        isSitter: this.accountData?.isSitter?.() || false,
        isParent: this.accountData?.isParent?.() || false,
        accountType: this.accountData?.accountType?.() || '',
      };
    },
  },
  watch: {
    '$route.params.care_type': {
      handler() {
        this.fetchProfileData();
      },
    },
    '$route.params.sitter_uuid': {
      handler() {
        this.fetchProfileData();
      },
    },
  },
  mounted() {
    this.fetchProfileData();
    this.fetchReviews();
  },
  methods: {
    handleViewMoreReviews(): void {
      this.reviewsFetchCount += 3;
      this.fetchReviews();
    },
    async fetchProfileData() {
      this.loading = true;
      const { careType, sitterUuid } = this.paramData;
      const careTypeQuery = profileQueryMap[careType.toLowerCase()];

      if (!careTypeQuery) {
        this.generalErrorType = GeneralErrorType.DEFAULT;
        console.error(`No query found for care type: ${careType}`); // eslint-disable-line no-console
        return;
      }

      await this.$apollo
        .query({
          query: careTypeQuery,
          variables: {
            accountId: sitterUuid,
          },
        })
        .then(({ data }) => {
          this.profileAccountData = data.account;
          const formattedCareType = FormattedCareType[careType.toUpperCase()];
          const careTypeProfile = this.profileAccountData.profiles[formattedCareType];

          if (!careTypeProfile.profile) {
            this.generalErrorType = GeneralErrorType.PROFILE_UNAVAILABLE;
          }

          // These fields are only populated for the owner of the profile
          if (careTypeProfile.profileCompleted === false || careTypeProfile.profileVisible === false) {
            this.bannerType = BannerType.PROFILE_HIDDEN;
          }

          console.log(this.profileAccountData); // eslint-disable-line no-console
        })
        .catch((error: any) => {
          console.error(error); // eslint-disable-line no-console

          if (error.message.includes('Account not found')) {
            this.bannerType = BannerType.ACCOUNT_NOT_FOUND;
            return;
          }

          this.generalErrorType = GeneralErrorType.DEFAULT;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    fetchReviews() {
      this.$apollo
        .query({
          query: FETCH_REVIEWS,
          variables: {
            accountId: this.paramData.sitterUuid,
            reviewsFetchCount: this.reviewsFetchCount,
          },
        })
        .then(({ data }) => {
          this.reviewsData = data.account.reviews;
        })
        .catch((error) => {
          console.error('Error fetching reviews:', error); // eslint-disable-line no-console
        });
    },
  },
};
</script>
